import { cjk } from "@streamdown/cjk";
import { createMathPlugin } from "@streamdown/math";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
} from "react";
import { Streamdown, type UrlTransform } from "streamdown";
import "katex/dist/katex.min.css";
import type { TextScientificSelection } from "./types";

const MAX_SELECTION_CHARACTERS = 16 * 1024;
const SELECTION_CONTEXT_CHARACTERS = 512;
const documentMath = createMathPlugin({ singleDollarTextMath: true });

function isSafeDocumentLink(href: string) {
  const value = href.trim();
  if (!value || value.startsWith("//")) return false;
  if (
    value.startsWith("#") ||
    value.startsWith("/") ||
    /^(?:https?:|mailto:|tel:)/iu.test(value)
  ) {
    return true;
  }
  return !/^[a-z][a-z0-9+.-]*:/iu.test(value);
}

function isSafeDocumentImage(src: string) {
  return (
    src.startsWith("data:image/") ||
    src.startsWith("blob:") ||
    isSafeDocumentLink(src)
  );
}

const documentUrlTransform: UrlTransform = (url, key) => {
  if (key === "href") return isSafeDocumentLink(url) ? url : null;
  if (key === "src") return isSafeDocumentImage(url) ? url : null;
  return null;
};

function SafeDocumentLink({
  href,
  children,
  node: _node,
  ...props
}: ComponentPropsWithoutRef<"a"> & { node?: unknown }) {
  if (!href || !isSafeDocumentLink(href)) {
    return <span className="blocked-link">{children}</span>;
  }
  const external = href.startsWith("http://") || href.startsWith("https://");
  return (
    <a
      {...props}
      href={href}
      rel={external ? "noreferrer noopener" : undefined}
      target={external ? "_blank" : undefined}
    >
      {children}
    </a>
  );
}

function SafeDocumentImage({
  src,
  alt,
  node: _node,
  ...props
}: ComponentPropsWithoutRef<"img"> & { node?: unknown }) {
  if (!src || !isSafeDocumentImage(src)) {
    return (
      <span className="blocked-image">
        图像路径不可直接预览{alt ? `：${alt}` : ""}
      </span>
    );
  }
  return <img {...props} src={src} alt={alt ?? ""} loading="lazy" />;
}

function ScrollableDocumentTable({
  children,
  node: _node,
  ...props
}: ComponentPropsWithoutRef<"table"> & { node?: unknown }) {
  return (
    <div
      className="artifact-document-table-wrap timeline-result-table-wrap"
      role="region"
      aria-label="文档表格，可横向滚动"
      tabIndex={0}
    >
      <table {...props}>{children}</table>
    </div>
  );
}

/**
 * A local reader for source-backed Markdown/text Artifacts. Its visual source
 * may omit presentation-only boilerplate, while all selection anchors remain
 * tied to the immutable source text used by the server-side Apply operation.
 */
export function DocumentReader({
  artifactId,
  sourceContent,
  content,
  selection,
  onSelectionChange,
}: {
  artifactId: string;
  sourceContent: string;
  content: string;
  selection?: TextScientificSelection;
  onSelectionChange(selection: TextScientificSelection): void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const lastSelectionRef = useRef<string>();
  const [pinnedRects, setPinnedRects] = useState<DocumentSelectionRect[]>([]);

  const clearSelection = useCallback(() => {
    if (lastSelectionRef.current === undefined && !selection?.quote) return;
    lastSelectionRef.current = undefined;
    onSelectionChange({
      renderer: "report",
      artifactId,
      quote: "",
      prefix: "",
      suffix: "",
    });
  }, [artifactId, onSelectionChange, selection?.quote]);

  const captureSelection = useCallback(() => {
    const root = rootRef.current;
    const browserSelection = window.getSelection();
    if (
      !root ||
      !browserSelection ||
      browserSelection.rangeCount !== 1 ||
      browserSelection.isCollapsed
    ) {
      clearSelection();
      return;
    }
    const range = browserSelection.getRangeAt(0);
    if (
      !root.contains(range.commonAncestorContainer) ||
      !root.contains(range.startContainer) ||
      !root.contains(range.endContainer)
    ) {
      return;
    }
    const renderedQuote = browserSelection
      .toString()
      .replace(/\s+/g, " ")
      .trim();
    if (
      !renderedQuote ||
      renderedQuote.length > MAX_SELECTION_CHARACTERS
    ) {
      clearSelection();
      return;
    }
    const sourceRange = exactDocumentSourceRange(sourceContent, renderedQuote);
    if (!sourceRange) {
      clearSelection();
      return;
    }
    const quote = sourceContent.slice(sourceRange.start, sourceRange.end);
    if (quote.length > MAX_SELECTION_CHARACTERS) {
      clearSelection();
      return;
    }
    const next: TextScientificSelection = {
      renderer: "report",
      artifactId,
      quote,
      prefix: sourceContent.slice(
        Math.max(0, sourceRange.start - SELECTION_CONTEXT_CHARACTERS),
        sourceRange.start,
      ),
      suffix: sourceContent.slice(
        sourceRange.end,
        Math.min(
          sourceContent.length,
          sourceRange.end + SELECTION_CONTEXT_CHARACTERS,
        ),
      ),
    };
    const signature = JSON.stringify(next);
    if (lastSelectionRef.current === signature) return;
    lastSelectionRef.current = signature;
    onSelectionChange(next);
  }, [artifactId, clearSelection, onSelectionChange, sourceContent]);

  useEffect(() => {
    lastSelectionRef.current = undefined;
  }, [artifactId, sourceContent, content]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const sourceRange =
      selection?.artifactId === artifactId
        ? verifiedDocumentSelectionRange(sourceContent, selection)
        : undefined;
    const range =
      root && sourceRange
        ? renderedDocumentSelectionRange(
            root,
            sourceContent.slice(sourceRange.start, sourceRange.end),
          )
        : undefined;
    if (!root || !range) {
      setPinnedRects([]);
      return;
    }

    let frame: number | undefined;
    const update = () => {
      frame = undefined;
      const rootBounds = root.getBoundingClientRect();
      const next = [...range.getClientRects()]
        .filter((rect) => rect.width > 0 && rect.height > 0)
        .map((rect) => ({
          left: rect.left - rootBounds.left + root.scrollLeft,
          top: rect.top - rootBounds.top + root.scrollTop,
          width: rect.width,
          height: rect.height,
        }));
      setPinnedRects((current) =>
        sameDocumentSelectionRects(current, next) ? current : next,
      );
    };
    const scheduleUpdate = () => {
      if (frame !== undefined) return;
      frame = window.requestAnimationFrame(update);
    };
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(scheduleUpdate);
    resizeObserver?.observe(root);
    update();
    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener("scroll", scheduleUpdate, true);
    return () => {
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener("scroll", scheduleUpdate, true);
    };
  }, [
    artifactId,
    sourceContent,
    selection?.artifactId,
    selection?.prefix,
    selection?.quote,
    selection?.suffix,
  ]);

  return (
    <div
      ref={rootRef}
      className="artifact-document-reader artifact-report-preview"
      data-testid="artifact-report-preview"
      data-document-renderer="streamdown"
      data-selection-pinned={pinnedRects.length > 0 ? "true" : "false"}
      onMouseUp={captureSelection}
      onKeyUp={(event) => {
        if (event.key === "Shift") captureSelection();
      }}
    >
      {pinnedRects.length > 0 && (
        <div className="artifact-report-selection-layer" aria-hidden="true">
          {pinnedRects.map((rect, index) => (
            <span
              className="artifact-report-selection-mark"
              data-testid="artifact-report-selection-mark"
              key={`${rect.left}-${rect.top}-${index}`}
              style={rect}
            />
          ))}
        </div>
      )}
      <Streamdown
        className="artifact-document-content open-science-agent-markdown"
        components={{
          a: SafeDocumentLink,
          img: SafeDocumentImage,
          table: ScrollableDocumentTable,
        }}
        controls={false}
        mode="static"
        plugins={{ cjk, math: documentMath }}
        rehypePlugins={[]}
        remarkRehypeOptions={{ allowDangerousHtml: false }}
        skipHtml
        urlTransform={documentUrlTransform}
      >
        {content}
      </Streamdown>
    </div>
  );
}

interface DocumentSelectionRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

function verifiedDocumentSelectionRange(
  content: string,
  selection: TextScientificSelection,
): { start: number; end: number } | undefined {
  if (!selection.quote) return undefined;
  const matches: Array<{ start: number; end: number }> = [];
  let start = content.indexOf(selection.quote);
  while (start !== -1) {
    const end = start + selection.quote.length;
    const prefixStart = Math.max(0, start - selection.prefix.length);
    const suffixEnd = Math.min(content.length, end + selection.suffix.length);
    if (
      content.slice(prefixStart, start) === selection.prefix &&
      content.slice(end, suffixEnd) === selection.suffix
    ) {
      matches.push({ start, end });
    }
    start = content.indexOf(selection.quote, start + 1);
  }
  return matches.length === 1 ? matches[0] : undefined;
}

function renderedDocumentSelectionRange(root: HTMLElement, quote: string) {
  const normalizedQuote = normalizeDocumentSelectionText(quote);
  if (!normalizedQuote) return undefined;
  const document = root.ownerDocument;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const positions: Array<{ node: Text; start: number; end: number }> = [];
  let rendered = "";
  let pendingWhitespace: { node: Text; start: number; end: number } | undefined;

  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    for (let offset = 0; offset < node.data.length; offset += 1) {
      const character = node.data[offset];
      if (/\s/.test(character)) {
        if (rendered && !pendingWhitespace) {
          pendingWhitespace = { node, start: offset, end: offset + 1 };
        }
        continue;
      }
      if (pendingWhitespace) {
        rendered += " ";
        positions.push(pendingWhitespace);
        pendingWhitespace = undefined;
      }
      rendered += character;
      positions.push({ node, start: offset, end: offset + 1 });
    }
  }

  const start = rendered.indexOf(normalizedQuote);
  if (
    start === -1 ||
    rendered.indexOf(normalizedQuote, start + normalizedQuote.length) !== -1
  ) {
    return undefined;
  }
  const end = start + normalizedQuote.length - 1;
  const startPosition = positions[start];
  const endPosition = positions[end];
  if (!startPosition || !endPosition) return undefined;
  const range = document.createRange();
  range.setStart(startPosition.node, startPosition.start);
  range.setEnd(endPosition.node, endPosition.end);
  return range;
}

function normalizeDocumentSelectionText(value: string) {
  return value.replace(/\s+/g, " ").trim();
}

function sameDocumentSelectionRects(
  current: DocumentSelectionRect[],
  next: DocumentSelectionRect[],
) {
  return (
    current.length === next.length &&
    current.every(
      (rect, index) =>
        Math.abs(rect.left - next[index].left) < 0.5 &&
        Math.abs(rect.top - next[index].top) < 0.5 &&
        Math.abs(rect.width - next[index].width) < 0.5 &&
        Math.abs(rect.height - next[index].height) < 0.5,
    )
  );
}

function exactDocumentSourceRange(
  source: string,
  renderedQuote: string,
): { start: number; end: number } | undefined {
  const directStart = source.indexOf(renderedQuote);
  if (
    directStart !== -1 &&
    source.indexOf(renderedQuote, directStart + renderedQuote.length) === -1
  ) {
    return {
      start: directStart,
      end: directStart + renderedQuote.length,
    };
  }

  const normalizedQuote = renderedQuote.replace(/\s+/g, " ").trim();
  const tokens = normalizedQuote.split(" ").filter(Boolean);
  if (!tokens.length) return undefined;
  const expression = tokens.map(escapeRegExp).join("\\s+");
  const matches = [...source.matchAll(new RegExp(expression, "g"))];
  if (matches.length !== 1 || matches[0].index === undefined) return undefined;
  return {
    start: matches[0].index,
    end: matches[0].index + matches[0][0].length,
  };
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
